/**
 * Guarantee Tabs Mixin for the Application slideover
 *
 * Issuing a guarantor's deed, and recording that it came back signed and
 * witnessed. Each guarantor has a tab of their own, so everything here is
 * addressed to one guarantee at a time — naming a new one is the Add a person
 * modal's job (see add_person_mixin.js).
 *
 * The deed is printed and wet-signed rather than e-signed: a deed's witness has
 * to be physically present to attest the guarantor's signature, and remote
 * witnessing is not valid in England and Wales. So the flow here is generate →
 * send → the guarantor signs on paper in front of somebody → we record what
 * happened and file the scan. Everything in `executionForm` is transcribed from
 * that piece of paper.
 *
 * Requirements on the consuming component:
 *   - `this.applicationId` — the application's ID (used for API endpoints)
 */

export function guaranteeTabMixin() {
  return {
    guaranteeData: {
      guarantor_required: false,
      outstanding_guarantee: false,
      applicants: [],
      relationships: [],
      deed_template_present: false,
      guarantees: []
    },
    guaranteesLoaded: false,
    guaranteesLoading: false,
    guaranteeError: null,
    requestingGuarantor: false,
    guarantorRequestNotice: null,

    executionForm: {
      open: false,
      guaranteeId: null,
      saving: false,
      guarantor_signed_on: '',
      executed_on: '',
      witness_name: '',
      witness_address: ''
    },

    // ========== Loading ==========

    async fetchGuarantees(force = false) {
      if (!this.applicationId) return
      if (this.guaranteesLoaded && !force) return

      this.guaranteesLoading = true
      this.guaranteeError = null
      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/guarantees`, {
          headers: { 'Accept': 'application/json' }
        })
        if (!response.ok) throw new Error('Could not load guarantors')
        this.guaranteeData = await response.json()
        this.guaranteesLoaded = true
      } catch (e) {
        this.guaranteeError = e.message
      } finally {
        this.guaranteesLoading = false
      }
    },

    // ========== Derived ==========

    get guarantees() {
      return this.guaranteeData.guarantees || []
    },

    get soleApplicant() {
      return (this.guaranteeData.applicants || []).length === 1
    },

    // The share of rent left unguaranteed. On a sole-tenant letting anything
    // below 100 means part of the rent is standing on nothing, which is almost
    // always a slip rather than a decision.
    get unguaranteedShare() {
      const covered = this.guarantees
        .filter(g => g.binding)
        .reduce((sum, g) => sum + Number(g.rent_liability_percentage || 0), 0)
      return Math.max(0, 100 - covered)
    },

    guaranteeStatusClass(guarantee) {
      if (guarantee.binding) return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      if (this.guaranteeSpent(guarantee)) {
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
      }
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    },

    // A guarantee that is over — turned down, or released. Nothing left to chase.
    guaranteeSpent(guarantee) {
      return guarantee.status === 'rejected' || guarantee.status === 'discharged'
    },

    // Whether this guarantor's tab should carry a dot: named, still live, but
    // the deed hasn't come back signed, so the guarantee secures nothing yet.
    guaranteeNeedsAttention(guarantee) {
      return !guarantee.binding && !this.guaranteeSpent(guarantee)
    },

    // The guarantee whose tab is open, if one is. The compliance mixin reads
    // this to anchor an uploaded document: a guarantor's checks belong to the
    // guarantee, not to a tenancy or an applicant row — they have neither.
    // Null on every other tab, which is what leaves applicants unaffected.
    get complianceGuaranteeId() {
      const match = /^guarantor_(\d+)$/.exec(this.activeAppTab || '')
      if (!match) return null

      return this.guarantees[Number(match[1]) - 1]?.id || null
    },

    formatPence(pence) {
      if (pence === null || pence === undefined || pence === '') return '—'
      return new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP', maximumFractionDigits: 0
      }).format(Number(pence) / 100)
    },

    // ========== The guarantor's portal invitation ==========

    async resendGuarantorInvitation(guaranteeId) {
      this.guaranteeError = null
      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/guarantees/${guaranteeId}/invite`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error((data.errors || ['Could not send the invitation']).join(', '))
        await this.fetchGuarantees(true)
      } catch (e) {
        this.guaranteeError = e.message
      }
    },

    // ========== Asking the applicant to name a guarantor ==========

    async requestGuarantor() {
      this.requestingGuarantor = true
      this.guarantorRequestNotice = null
      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/request_guarantor`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.error || 'Could not send the request')
        this.application = { ...this.application, ...data }
        this.guarantorRequestNotice = 'Emailed the applicant a link to add their guarantor.'
        await this.fetchGuarantees(true)
      } catch (e) {
        this.guarantorRequestNotice = e.message
      } finally {
        this.requestingGuarantor = false
      }
    },

    // ========== Removing a guarantor ==========

    async removeGuarantor(guaranteeId) {
      this.guaranteeError = null
      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/guarantees/${guaranteeId}`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        if (!response.ok) throw new Error('Could not remove guarantor')
        // Their tab has just gone. Standing on a tab that no longer exists
        // shows an empty panel, so fall back to where the warning lives.
        this.activeAppTab = 'summary'
        await this.fetchGuarantees(true)
      } catch (e) {
        this.guaranteeError = e.message
      }
    },

    // ========== The deed ==========

    async generateDeed(guaranteeId) {
      this.guaranteeError = null
      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/guarantees/${guaranteeId}/generate_deed`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        const data = await response.json()
        if (!response.ok) throw new Error((data.errors || ['Could not generate the deed']).join(', '))
        await this.fetchGuarantees(true)
      } catch (e) {
        this.guaranteeError = e.message
      }
    },

    openExecutionForm(guarantee) {
      this.executionForm.open = true
      this.executionForm.guaranteeId = guarantee.id
      this.executionForm.guarantor_signed_on = guarantee.guarantor_signed_on || ''
      this.executionForm.executed_on = guarantee.executed_on || ''
      this.executionForm.witness_name = guarantee.witness_name || ''
      this.executionForm.witness_address = guarantee.witness_address || ''
      this.guaranteeError = null
    },

    async saveExecution() {
      this.executionForm.saving = true
      this.guaranteeError = null
      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/guarantees/${this.executionForm.guaranteeId}/mark_executed`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            guarantor_signed_on: this.executionForm.guarantor_signed_on,
            executed_on: this.executionForm.executed_on || this.executionForm.guarantor_signed_on,
            witness_name: this.executionForm.witness_name,
            witness_address: this.executionForm.witness_address
          })
        })
        const data = await response.json()
        if (!response.ok) throw new Error((data.errors || ['Could not record execution']).join(', '))

        this.executionForm.open = false
        await this.fetchGuarantees(true)
        // The gate on approve_referencing! has just moved — refresh the
        // application so the rest of the slideover agrees.
        if (this.fetchApplication) await this.fetchApplication()
      } catch (e) {
        this.guaranteeError = e.message
      } finally {
        this.executionForm.saving = false
      }
    },

    async uploadExecutedDeed(guaranteeId, event) {
      const file = event.target.files[0]
      if (!file) return

      this.guaranteeError = null
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await fetch(`/tenancy-applications/${this.applicationId}/guarantees/${guaranteeId}/upload_executed_deed`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
          body: formData
        })
        const data = await response.json()
        if (!response.ok) throw new Error((data.errors || ['Could not upload the signed deed']).join(', '))
        event.target.value = ''
        await this.fetchGuarantees(true)
      } catch (e) {
        this.guaranteeError = e.message
      }
    }
  }
}
